// Shared "preview a bell" playback used by the Add Bell modal and the bell
// table/search players. It enforces the same rules everywhere:
//   * Priority 1: play the backend (R2) URL from the API response.
//   * Priority 2: fall back to the bundled /public/bells sound only when the
//     remote source cannot be played.
//   * Never play two sounds at once — the caller holds one controller and can
//     stop() it before starting another.

import { resolveBellSources } from "../constants/BellSounds";

// Only one bell preview may ever be audible at a time across the whole app. When
// a new preview starts we stop whatever was playing and notify its owner (via
// onStop) so its UI can reset to the "play" state.
let activeController = null;

// Start playing a bell preview. `bell` is a url/path string. The callbacks let
// the caller reflect play/stop state in its UI:
//   onStart  - a source began playing
//   onEnded  - playback finished naturally
//   onError  - no source could be played
//   onStop   - playback was stopped (by the caller, or interrupted by another
//              preview starting)
// Returns a controller whose stop() halts playback.
export const playBellPreview = (bell, { onStart, onEnded, onError, onStop } = {}) => {
    // Enforce the single-preview rule.
    if (activeController) {
        activeController.stop();
    }

    const sources = resolveBellSources(bell);
    let index = 0;
    let audio = null;
    let stopped = false;

    const controller = {
        stop() {
            if (stopped) {
                return;
            }
            stopped = true;
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
                audio = null;
            }
            if (activeController === controller) {
                activeController = null;
            }
            onStop && onStop();
        },
    };

    const finish = (cb) => {
        stopped = true;
        audio = null;
        if (activeController === controller) {
            activeController = null;
        }
        cb && cb();
    };

    const playNext = () => {
        if (stopped) {
            return;
        }
        if (index >= sources.length) {
            finish(onError);
            return;
        }

        const el = new Audio(sources[index++]);
        // Always preview at full loudness.
        el.volume = 1;
        el.muted = false;
        audio = el;
        let advanced = false;

        // On any failure of this source, move on to the next candidate once.
        const advance = () => {
            if (advanced || stopped) {
                return;
            }
            advanced = true;
            playNext();
        };

        el.onended = () => {
            if (!stopped && audio === el) {
                finish(onEnded);
            }
        };
        el.onerror = advance;
        el.play()
            .then(() => {
                if (!stopped) {
                    onStart && onStart();
                }
            })
            .catch(advance);
    };

    activeController = controller;
    playNext();

    return controller;
};
